import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext.js'
import './LanguageManagement.css'

// All supported languages with flags
const ALL_LANGUAGES = [
  { code: 'de', name: 'Deutsch',    flag: '🇩🇪' },
  { code: 'en', name: 'English',    flag: '🇬🇧' },
  { code: 'es', name: 'Español',    flag: '🇪🇸' },
  { code: 'it', name: 'Italiano',   flag: '🇮🇹' },
  { code: 'pl', name: 'Polski',     flag: '🇵🇱' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'da', name: 'Dansk',      flag: '🇩🇰' },
  { code: 'fr', name: 'Français',   flag: '🇫🇷' },
  { code: 'hu', name: 'Magyar',     flag: '🇭🇺' },
  { code: 'hr', name: 'Hrvatski',   flag: '🇭🇷' },
  { code: 'pt', name: 'Português',  flag: '🇵🇹' },
  { code: 'cs', name: 'Čeština',    flag: '🇨🇿' },
  { code: 'bg', name: 'Български',  flag: '🇧🇬' },
  { code: 'et', name: 'Eesti',      flag: '🇪🇪' },
  { code: 'fi', name: 'Suomi',      flag: '🇫🇮' },
  { code: 'el', name: 'Ελληνικά',   flag: '🇬🇷' },
  { code: 'lv', name: 'Latviešu',   flag: '🇱🇻' },
  { code: 'lt', name: 'Lietuvių',   flag: '🇱🇹' },
  { code: 'no', name: 'Norsk',      flag: '🇳🇴' },
  { code: 'ro', name: 'Română',     flag: '🇷🇴' },
  { code: 'ru', name: 'Русский',    flag: '🇷🇺' },
  { code: 'sr', name: 'Српски',     flag: '🇷🇸' },
  { code: 'sk', name: 'Slovenčina', flag: '🇸🇰' },
  { code: 'sl', name: 'Slovenščina',flag: '🇸🇮' },
  { code: 'sv', name: 'Svenska',    flag: '🇸🇪' },
  { code: 'tr', name: 'Türkçe',     flag: '🇹🇷' },
  { code: 'uk', name: 'Українська', flag: '🇺🇦' },
]

const LanguageManagement = () => {
  const { apiFetch, admin, isOwner } = useAuth()

  const [tenants, setTenants]               = useState([])
  const [selectedTenant, setSelectedTenant] = useState('')
  const [enabledLangs, setEnabledLangs]     = useState([])
  const [loading, setLoading]               = useState(false)
  const [saving, setSaving]                 = useState(false)
  const [error, setError]                   = useState(null)
  const [success, setSuccess]               = useState(false)
  const [addedLangs, setAddedLangs]         = useState([])
  const savedLangsRef                       = useRef([])

  useEffect(() => {
    if (isOwner) {
      apiFetch('/api/admin/tenants')
        .then(r => r.json())
        .then(data => {
          setTenants(data)
          if (data.length > 0) setSelectedTenant(data[0].id)
        })
    } else {
      setSelectedTenant(admin.tenant_id)
    }
  }, [isOwner])

  useEffect(() => {
    if (!selectedTenant) return
    setLoading(true)
    setError(null)
    apiFetch(`/api/admin/languages/${selectedTenant}`)
      .then(r => r.json())
      .then(data => {
        const langs = data.enabled_languages || ['de', 'en']
        setEnabledLangs(langs)
        savedLangsRef.current = langs
        setAddedLangs([])
      })
      .catch(() => setError('Spracheinstellungen konnten nicht geladen werden'))
      .finally(() => setLoading(false))
  }, [selectedTenant])

  const toggleLanguage = (code) => {
    // German must always be enabled — it's used for the PDF
    if (code === 'de') return
    setEnabledLangs(prev =>
      prev.includes(code)
        ? prev.filter(l => l !== code)
        : [...prev, code]
    )
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    setSuccess(false)
    try {
      const res = await apiFetch(`/api/admin/languages/${selectedTenant}`, {
        method: 'PUT',
        body: JSON.stringify({ enabled_languages: enabledLangs }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      const newlyAdded = enabledLangs.filter(l => !savedLangsRef.current.includes(l))
      savedLangsRef.current = enabledLangs
      setAddedLangs(newlyAdded)
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div className="page-header">
        <h1>Sprachen</h1>
        <p>Legen Sie fest, welche Sprachen Besucher auswählen können</p>
      </div>

      {isOwner && tenants.length > 0 && (
        <div className="card lang-tenant-select">
          <div className="form-group lang-tenant-form-group">
            <label>Mandant</label>
            <select
              className="form-control"
              value={selectedTenant}
              onChange={e => setSelectedTenant(e.target.value)}
            >
              {tenants.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">✓ Spracheinstellungen gespeichert</div>}
      {addedLangs.length > 0 && (
        <div className="alert alert-info lang-safety-rules-hint">
          Neue Sprachen hinzugefügt ({addedLangs.map(l => ALL_LANGUAGES.find(x => x.code === l)?.name || l.toUpperCase()).join(', ')}):
          Die Sicherheitsregeln müssen erneut gespeichert werden, damit Übersetzungen in diesen Sprachen erzeugt werden.
        </div>
      )}

      <div className="card">
        <div className="section-title">Verfügbare Sprachen</div>
        <p className="lang-hint">
          Deutsch ist immer aktiviert (wird für das Compliance-PDF benötigt).
          Derzeit aktiviert: <strong className="lang-hint-count">{enabledLangs.length}</strong> von {ALL_LANGUAGES.length}
        </p>

        {loading ? (
          <div className="loading-state"><span className="spinner" /></div>
        ) : (
          <div className="lang-grid">
            {ALL_LANGUAGES.map(lang => {
              const isEnabled = enabledLangs.includes(lang.code)
              const isForced  = lang.code === 'de'
              return (
                <div
                  key={lang.code}
                  className={`lang-item ${isEnabled ? 'enabled' : ''} ${isForced ? 'forced' : ''}`}
                  onClick={() => toggleLanguage(lang.code)}
                >
                  <span className="lang-flag">{lang.flag}</span>
                  <span className="lang-name">{lang.name}</span>
                  <span className="lang-code">{lang.code.toUpperCase()}</span>
                  <label className="toggle" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={() => toggleLanguage(lang.code)}
                      disabled={isForced}
                    />
                    <span className="toggle-slider" />
                  </label>
                </div>
              )
            })}
          </div>
        )}

        <div className="lang-save-row">
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? <><span className="spinner spinner-sm" /> Speichern...</> : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default LanguageManagement
